import {
  Component, OnInit, OnChanges, Input, Output, EventEmitter, SimpleChanges,
  ViewChild, ElementRef, OnDestroy, ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Subject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomerService } from './../../api/customer.service';
import { FormService } from './../../api/form.service';
import { FeeSplitsService } from './../../api/fee-splits.service';
import uuid from '../../utils/uuid';

@UntilDestroy()
@Component({
  selector: 'iv-broker-search-field',
  templateUrl: './broker-search-field.component.html',
  styleUrls: ['./broker-search-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BrokerSearchFieldComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('brokerInput') brokerInput: ElementRef;
  @ViewChild('externalBrokerInput') externalBrokerInput: ElementRef;
  @Input() index = 0;
  @Input() externalBroker = false;
  @Input() billExternal = false;
  @Input() isExternal = false;
  @Input() showExternal = true;
  @Input() showEmail = false;
  @Input() isFeeInfluencer = false;
  @Input() viewOnly = false;
  @Input() checkboxLabel;
  @Input() broker;
  @Input() brokerDetails;
  @Input() name;
  @Input() email;
  @Input() showBrokerSearchLabel = true;
  @Input() crossBorder = false;
  @Input() disabled = false;

  @Output() selectBroker = new EventEmitter();

  uuid = uuid();
  brokerForm;
  displayForContactName;
  functionalUnit;
  brokerInitials = '';

  brokerSuggestions$: Observable<any[]>;
  brokerSearch = new Subject<string>();

  constructor(
    private customerService: CustomerService,
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private feeSplitsService: FeeSplitsService
  ) { }

  ngOnInit() {
    this.setupForm();
    // Get initials values
    this.brokerSearch.next('');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['billExternal'] && !changes['billExternal'].firstChange) {
      // Reset UI values
      this.displayForContactName = '';
      this.name = '';
      this.brokerInitials = '';
      this.brokerForm.get('name').setValue('');
    }

    // Important - We need to call setExternalFlag first
    this.setExternalFlag();
    this.setupForm();
  }

  setExternalFlag() {
    if (this.isFeeInfluencer) {
      this.isExternal = this.externalBroker;
    } else {
      this.isExternal = this.billExternal;
    }
  }

  setupForm() {
    this.setExternalFlag();
    const params = {
      broker_details: [this.brokerDetails],
      name: [{ value: this.name, disabled: this.viewOnly || (this.crossBorder && this.isExternal) || this.disabled }],
      email: [this.email],
      is_external: [this.isExternal]
    };
    this.brokerForm = this.fb.group(params);
    this.formService.addLoadedForm(this.brokerForm, this.uuid);
    // this.showFunctionalUnitOfContact();
    this.showContactName();
    this.setupBrokerSuggestions();
    this.setupFormSubscription();
    this.getBrokerInitials();
  }

  setupBrokerSuggestions() {
    this.brokerSuggestions$ = this.brokerSearch.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(
        brokerName => {
          if (this.isExternal === true) {
            return brokerName ? this.customerService.searchContacts('name', brokerName) : of<any[]>([]);
          } else {
            return brokerName ? this.customerService.searchEmployees(brokerName) : of<any[]>([]);
          }
        }
      ));
  }

  setupFormSubscription() {
    this.brokerForm
      .valueChanges
      .pipe(untilDestroyed(this),
        debounceTime(850),
        distinctUntilChanged())
      .subscribe(changes => {
        this.getBrokerInitials(changes.broker_details, changes.name);
        if (this.isFeeInfluencer) {
          this.selectBroker.emit({ ...changes });
        }
      });
  }

  getSuggestions(brokerName: string, event?: any) {
    if (event.key === 'Enter' && brokerName !== '' && !this.isFeeInfluencer) {
      this.selectBroker.emit({ ...this.brokerForm.value });
    } else {
      this.brokerSearch.next(brokerName);
    }
  }

  brokerSelectionChange(customer?) {
    if (customer) {
      this.name = customer.name;
      this.email = customer.email;
      this.brokerForm.patchValue({
        broker_details: customer,
        name: customer.name,
        email: customer.email || ''
      });
    }
    if (this.isExternal) {
      this.externalBrokerInput = customer ? customer.name : '';
    } else {
      this.brokerInput = customer ? customer.name : '';
    }
    this.brokerForm.patchValue({
      is_external: this.isExternal
    });
    this.selectBroker.emit({ ...this.brokerForm.value });
  }

  getBrokerInitials(brokerDetails?, brokerName?) {
    let name = '';
    let initials = '';

    if (this.externalBroker === true || this.billExternal === true) {
      name = (brokerName ? brokerName : this.name);
    } else {
      const newBrokerDetails = (brokerDetails ? this.getBrokerDetails(brokerDetails) : this.getBrokerDetails(this.brokerDetails));
      name = newBrokerDetails.name;
    }

    if (name) {
      const names = name.split(' ');
      initials = (Array.isArray(names) ? names[0].substring(0, 1).toUpperCase() : name.substring(0, 1).toUpperCase());

      if (names.length > 1) {
        initials += names[names.length - 1].substring(0, 1).toUpperCase();
      }
    }

    this.brokerInitials = initials;
  }

  showContactName() {
    this.displayForContactName = '';
    if (this.isExternal) {
      this.displayForContactName = this.name;
    } else if (this.brokerForm.get('broker_details')) {
      const brokerDetails = this.getBrokerDetails(this.brokerForm.get('broker_details').value);
      this.displayForContactName = brokerDetails.preferred_name ? brokerDetails.preferred_name : '';
    }
  }

  /*  showFunctionalUnitOfContact() {
     if (!this.isExternal && this.brokerForm.get('broker_details') && this.brokerForm.get('broker_details').value
       && this.brokerForm.get('broker_details').value.functional_unit && this.brokerForm.get('broker_details').value.functional_unit.name) {
       this.displayForContactName = this.brokerForm.get('broker_details').value.functional_unit.name;
     } else if (this.isExternal) {
       if (this.brokerInput && this.brokerInput.nativeElement && this.brokerInput.nativeElement.value) {
         this.displayForContactName = this.brokerInput.nativeElement.value;
       } else {
         this.displayForContactName = this.name;
       }
     } else {
       this.displayForContactName = '';
     }
   } */

  externalContactChanged() {
    this.brokerForm.patchValue({
      name: this.name
    });
  }

  onBlurSaveBroker(brokerName) {
    if (brokerName !== '' && brokerName !== this.name && !this.isFeeInfluencer) {
      this.selectBroker.emit({ ...this.brokerForm.value });
    }
  }

  ngOnDestroy() {
  }

  getBrokerDetails(brokerDetails: any) {
    return this.feeSplitsService.getBrokerDetails(brokerDetails);
  }

}
